/**
 * What: Draws a Worker-sent `upload` block as the comparison image tile: a 100px square media cell (the house art and
 *   "Add image" when empty, the picture when set), the label and the file name in the details column, drag-and-drop and
 *   click-to-pick over the whole media cell, and the Replace and Remove buttons in one actions cell. The original-product
 *   tile (no `remove`) is drawn read-only — the label and picture only.
 * Transport / data-flow: The page owns upload → finalization → accepted evaluation as one lifecycle action.
 * This renderer selects files and draws the server's labels plus the chosen file's transient name. It owns
 * no request, validation policy, cancellation controller or upload busy state.
 */
import { useRef } from 'react'
import type { BuilderBlock } from '@testfeed/shopify-contracts'
import { COMPARISON_ART } from '../imagery/houseArt'
import './UploadBlock.css'
import { RequiredMark } from './RequiredMark'
import type { OnEdit, OnUpload } from './SectionBlocks'

type Block = Extract<BuilderBlock, { kind: 'upload' }>

export function UploadBlock({
  block,
  onEdit,
  onUpload,
  upload,
  disabled,
  value,
  error,
  labelHidden = false,
}: {
  block: Block
  onEdit: OnEdit
  onUpload: OnUpload
  upload: { key: string; fileName: string } | null
  disabled: boolean
  value: string
  /** The Worker's message for this field, already withheld while the field has an unanswered edit (`visibleMessage`). */
  error: string | undefined
  /** The label is drawn elsewhere (a group badge); keep it on the input for assistive tech but hide the heading. */
  labelHidden?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const uploading = upload?.key === block.key
  const { labels, remove } = block
  // A tile is editable (click-to-pick, drag-and-drop, Replace + Remove) exactly when the Worker sent a Remove action on it.
  const editable = remove !== null
  const locked = disabled || uploading

  const send = (file: File) => onUpload(block.key, file)

  const shownError = error
  // Completion includes accepted evaluation, so the server supplies the stored filename immediately.
  const filename = uploading ? upload.fileName : block.filename || null

  const media = (
    <div
      className={[
        'tf-comparison-image-row__media',
        uploading ? 'tf-comparison-image-row__media--uploading' : '',
        shownError ? 'tf-comparison-image-row__media--error' : '',
        value ? '' : 'tf-comparison-image-row__media--empty',
      ]
        .filter(Boolean)
        .join(' ')}
      title={block.label}
    >
      {value ? (
        <img alt={labels.alt} src={value} />
      ) : (
        <s-stack direction="block" gap="small-100" alignItems="center">
          <img
            data-testid="comparison-image-tile-splash"
            src={COMPARISON_ART}
            alt=""
            className="tf-comparison-image-row__splash"
          />
          <s-text color="subdued">{labels.choose}</s-text>
        </s-stack>
      )}
    </div>
  )

  // Editable: the media cell is a label wrapping a visually-hidden file input, so a click anywhere on it opens the picker and
  // keyboard focus lands on it (the CSS draws the focus ring). Drag-and-drop always cancels the browser's own file-open (even
  // while locked, so a drop never navigates the tab away); the Worker validates the file and sends refusal words.
  // Read-only (the original tile): the picture alone.
  const mediaCell = editable ? (
    <label
      data-testid="comparison-image-tile-dropzone"
      className={['tf-comparison-image-row__dropzone', locked ? 'tf-comparison-image-row__dropzone--disabled' : '']
        .filter(Boolean)
        .join(' ')}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        if (locked) return
        const file = event.dataTransfer.files[0]
        if (file) void send(file)
      }}
    >
      <input
        ref={input}
        type="file"
        accept={block.accept}
        aria-label={`${value ? labels.replace : labels.choose} ${block.label}`}
        className="tf-comparison-image-row__file-input"
        disabled={locked}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0]
          event.currentTarget.value = ''
          if (file) void send(file)
        }}
      />
      {media}
    </label>
  ) : (
    media
  )

  return (
    <div className="tf-comparison-image-row" data-testid="comparison-image-row">
      <div className="tf-comparison-image-row__media-cell">{mediaCell}</div>
      <div className="tf-comparison-image-row__details">
        <s-stack direction="block" gap="small-100">
          {labelHidden ? null : (
            <s-stack direction="inline" gap="small-100" alignItems="center">
              <s-text type="strong">{block.label}</s-text>
              {block.requiredReason ? <RequiredMark markKey={block.key} reason={block.requiredReason} /> : null}
            </s-stack>
          )}
          {/* The required reason read out for assistive tech, since the "*" above is visual only. */}
          {block.requiredReason ? <s-text accessibilityVisibility="exclusive">{block.requiredReason}</s-text> : null}
          {filename ? <s-text color="subdued">{filename}</s-text> : null}
          {!value && editable && !uploading ? <s-text color="subdued">{labels.empty}</s-text> : null}
          {uploading ? <s-text color="subdued">{labels.uploading}</s-text> : null}
          {shownError ? (
            <div role="alert">
              <s-text tone="critical">{shownError}</s-text>
            </div>
          ) : null}
        </s-stack>
      </div>
      {editable ? (
        <div className="tf-comparison-image-row__actions">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-button variant="secondary" disabled={locked} onClick={() => input.current?.click()}>
              {value ? labels.replace : labels.choose}
            </s-button>
            {remove ? (
              <s-button
                variant="tertiary"
                tone="critical"
                disabled={locked}
                accessibilityLabel={remove.accessibilityLabel}
                onClick={() => onEdit(remove.key, 'press')}
              >
                {remove.label}
              </s-button>
            ) : null}
          </s-stack>
        </div>
      ) : null}
    </div>
  )
}
